<template>
  <div class="category-manager">
    <div class="manager-header">
      <h2>Learning Categories</h2>
      <button @click="showCreateModal = true" class="btn btn-primary">
        <i class="fas fa-plus"></i>
        Add Category
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading categories...</p>
    </div>

    <!-- Categories List -->
    <div v-else class="categories-list">
      <div
        v-for="category in categories"
        :key="category.id"
        class="category-item"
      >
        <div class="category-info">
          <div class="category-header">
            <span class="category-icon" v-if="category.icon">{{ category.icon }}</span>
            <h3>{{ category.name }}</h3>
            <span v-if="category.parent_name" class="parent-badge">
              Sub-category of: {{ category.parent_name }}
            </span>
          </div>
          <p v-if="category.description" class="category-description">
            {{ category.description }}
          </p>
          <div class="category-meta">
            <span class="course-count">
              <i class="fas fa-book"></i>
              {{ category.course_count || 0 }} courses
            </span>
            <span class="category-slug">{{ category.slug }}</span>
          </div>
        </div>
        <div class="category-actions">
          <button @click="editCategory(category)" class="btn btn-small btn-secondary">
            <i class="fas fa-edit"></i>
            Edit
          </button>
          <button @click="deleteCategory(category)" class="btn btn-small btn-danger">
            <i class="fas fa-trash"></i>
            Delete
          </button>
        </div>
      </div>

      <div v-if="categories.length === 0" class="empty-state">
        <i class="fas fa-folder-open"></i>
        <h3>No categories yet</h3>
        <p>Create your first learning category to organize courses</p>
        <button @click="showCreateModal = true" class="btn btn-primary">
          <i class="fas fa-plus"></i>
          Create Category
        </button>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showCreateModal || editingCategory" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>{{ editingCategory ? 'Edit Category' : 'Create Category' }}</h2>
          <button @click="closeModal" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="form-group">
            <label>Name *</label>
            <input
              v-model="formData.name"
              type="text"
              placeholder="e.g., Veterinary Medicine"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label>Slug *</label>
            <input
              v-model="formData.slug"
              type="text"
              placeholder="e.g., veterinary-medicine"
              class="form-input"
            />
            <small>URL-friendly identifier (auto-generated from name if left empty)</small>
          </div>

          <div class="form-group">
            <label>Description</label>
            <textarea
              v-model="formData.description"
              placeholder="Describe this category..."
              class="form-textarea"
              rows="3"
            ></textarea>
          </div>

          <div class="form-group">
            <label>Parent Category</label>
            <select v-model="formData.parent" class="form-select">
              <option :value="null">None (Top-level category)</option>
              <option
                v-for="cat in categories.filter(c => c.id !== editingCategory?.id)"
                :key="cat.id"
                :value="cat.id"
              >
                {{ cat.name }}
              </option>
            </select>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Icon</label>
              <input
                v-model="formData.icon"
                type="text"
                placeholder="🎓 or fa-graduation-cap"
                class="form-input"
              />
            </div>

            <div class="form-group">
              <label>Color</label>
              <input
                v-model="formData.color"
                type="color"
                class="form-input"
              />
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeModal" class="btn btn-secondary">
            Cancel
          </button>
          <button
            @click="saveCategory"
            :disabled="!formData.name"
            class="btn btn-primary"
          >
            <i class="fas fa-save"></i>
            {{ editingCategory ? 'Update' : 'Create' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { lmsAPI } from '@/services/lmsAPI.js'
import { useToast } from 'vue-toastification'

export default {
  name: 'CategoryManager',
  setup() {
    const toast = useToast()
    const categories = ref([])
    const loading = ref(true)
    const showCreateModal = ref(false)
    const editingCategory = ref(null)
    const formData = ref({
      name: '',
      slug: '',
      description: '',
      parent: null,
      icon: '',
      color: '#3B82F6'
    })

    const loadCategories = async () => {
      loading.value = true
      try {
        const response = await lmsAPI.getCategories()
        categories.value = response.data
      } catch (err) {
        console.error('Error loading categories:', err)
        toast.error('Failed to load categories')
      } finally {
        loading.value = false
      }
    }

    const editCategory = (category) => {
      editingCategory.value = category
      formData.value = {
        name: category.name,
        slug: category.slug,
        description: category.description || '',
        parent: category.parent || null,
        icon: category.icon || '',
        color: category.color || '#3B82F6'
      }
    }

    const deleteCategory = async (category) => {
      if (!confirm(`Are you sure you want to delete "${category.name}"?`)) return

      try {
        await lmsAPI.deleteCategory(category.id)
        toast.success('Category deleted successfully')
        loadCategories()
      } catch (err) {
        console.error('Error deleting category:', err)
        toast.error('Failed to delete category')
      }
    }

    const saveCategory = async () => {
      try {
        // Auto-generate slug from name if not provided
        if (!formData.value.slug && formData.value.name) {
          formData.value.slug = formData.value.name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '')
        }

        if (editingCategory.value) {
          await lmsAPI.updateCategory(editingCategory.value.id, formData.value)
          toast.success('Category updated successfully')
        } else {
          await lmsAPI.createCategory(formData.value)
          toast.success('Category created successfully')
        }

        closeModal()
        loadCategories()
      } catch (err) {
        console.error('Error saving category:', err)
        toast.error('Failed to save category')
      }
    }

    const closeModal = () => {
      showCreateModal.value = false
      editingCategory.value = null
      formData.value = {
        name: '',
        slug: '',
        description: '',
        parent: null,
        icon: '',
        color: '#3B82F6'
      }
    }

    onMounted(() => {
      loadCategories()
    })

    return {
      categories,
      loading,
      showCreateModal,
      editingCategory,
      formData,
      editCategory,
      deleteCategory,
      saveCategory,
      closeModal
    }
  }
}
</script>

<style scoped>
.category-manager {
  width: 100%;
}

.manager-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-xl);
}

.manager-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--color-text-primary);
}

.loading-container {
  text-align: center;
  padding: var(--spacing-xxl);
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: var(--color-primary);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto var(--spacing-md);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.categories-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.category-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-lg);
  background: #f8f9fa;
  border-radius: var(--border-radius);
  border-left: 4px solid var(--color-primary);
  transition: all 0.2s ease;
}

.category-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.category-info {
  flex: 1;
}

.category-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-sm);
}

.category-icon {
  font-size: 1.5rem;
}

.category-header h3 {
  margin: 0;
  font-size: 1.2rem;
  color: var(--color-text-primary);
}

.parent-badge {
  font-size: 0.85rem;
  padding: 4px 12px;
  background: rgba(var(--color-primary-rgb), 0.1);
  color: var(--color-primary);
  border-radius: 12px;
}

.category-description {
  color: var(--color-text-secondary);
  margin: 0 0 var(--spacing-sm) 0;
  font-size: 0.95rem;
}

.category-meta {
  display: flex;
  gap: var(--spacing-lg);
  font-size: 0.9rem;
  color: var(--color-text-secondary);
}

.course-count {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.category-slug {
  font-family: monospace;
  background: rgba(0, 0, 0, 0.05);
  padding: 2px 8px;
  border-radius: 4px;
}

.category-actions {
  display: flex;
  gap: var(--spacing-sm);
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--border-radius);
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-small {
  padding: var(--spacing-xs) var(--spacing-md);
  font-size: 0.9rem;
}

.btn-primary {
  background: var(--color-primary);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-secondary);
  transform: translateY(-2px);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  color: var(--color-primary);
  border: 2px solid var(--color-primary);
}

.btn-secondary:hover {
  background: var(--color-primary);
  color: white;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover {
  background: #c82333;
}

.empty-state {
  text-align: center;
  padding: var(--spacing-xxl);
  background: #f8f9fa;
  border-radius: var(--border-radius);
}

.empty-state i {
  font-size: 4rem;
  color: var(--color-text-secondary);
  opacity: 0.5;
  margin-bottom: var(--spacing-lg);
}

.empty-state h3 {
  margin: 0 0 var(--spacing-sm) 0;
  color: var(--color-text-primary);
}

.empty-state p {
  color: var(--color-text-secondary);
  margin: 0 0 var(--spacing-lg) 0;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-lg);
}

.modal-content {
  background: white;
  border-radius: var(--border-radius);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  padding: var(--spacing-xl);
  border-bottom: 2px solid var(--color-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--color-text-secondary);
  padding: var(--spacing-sm);
}

.modal-close:hover {
  color: var(--color-text-primary);
}

.modal-body {
  padding: var(--spacing-xl);
}

.form-group {
  margin-bottom: var(--spacing-lg);
}

.form-group label {
  display: block;
  margin-bottom: var(--spacing-xs);
  font-weight: 600;
  color: var(--color-text-primary);
}

.form-group small {
  display: block;
  margin-top: var(--spacing-xs);
  color: var(--color-text-secondary);
  font-size: 0.85rem;
}

.form-input,
.form-textarea,
.form-select {
  width: 100%;
  padding: var(--spacing-sm);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius);
  font-size: 1rem;
}

.form-input:focus,
.form-textarea:focus,
.form-select:focus {
  outline: none;
  border-color: var(--color-primary);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-md);
}

.modal-footer {
  padding: var(--spacing-lg) var(--spacing-xl);
  border-top: 1px solid var(--color-border);
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-md);
}

@media (max-width: 768px) {
  .category-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .category-actions {
    width: 100%;
    margin-top: var(--spacing-md);
  }

  .category-actions .btn {
    flex: 1;
  }

  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>

